import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  errorCodeSelector,
  locationsSelector,
  totalSelector,
  pageSelector,
  noMoreSelector,
  locationSelector,
} from './store/selector';
import { PutLocationPayloadRequest } from './interfaces/put-location';
import { locationApiService } from './services/api';
import {
  getLocationsInitApiAction,
  getLocationsSuccessApiAction,
  getLocationsFailureApiAction,
  postLocationInitApiAction,
  postLocationFailuireApiAction,
  putLocationInitApiAction,
  putLocationFailuireApiAction,
  deleteLocationInitApiAction,
  deleteLocationFailuireApiAction,
  setLocationAction,
  increasePageAction,
  postLocationSuccessApiAction,
  deleteLocationSuccessApiAction,
  putLocationSuccessApiAction,
  businessClearAction,
} from './store/reducer';
import { PostLocationPayloadRequest } from './interfaces/post-location';
import { Location } from '../../interfaces/location.interface';

export function useLocationCore() {
  const locations = useSelector(locationsSelector);
  const location = useSelector(locationSelector);
  const total = useSelector(totalSelector);
  const page = useSelector(pageSelector);
  const noMore = useSelector(noMoreSelector);
  const errorCode = useSelector(errorCodeSelector);
  const dispatch = useDispatch();

  const getLocations = useCallback(
    async (
      id: number,
      filters?: {
        code?: string;
        warehouse_code?: string;
        location_type_id?: string;
        channel_type_id?: string;
      },
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(getLocationsInitApiAction());
        const response = await locationApiService.getLocations(id, filters);
        dispatch(getLocationsSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          getLocationsFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const postLocation = useCallback(
    async (
      id: number,
      body: PostLocationPayloadRequest,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(postLocationInitApiAction());
        await locationApiService.postLocation(id, body);
        dispatch(postLocationSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          postLocationFailuireApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const putLocation = useCallback(
    async (
      cId: number,
      pId: number,
      body: PutLocationPayloadRequest,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(putLocationInitApiAction());
        await locationApiService.putLocation(cId, pId, body);
        dispatch(putLocationSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          putLocationFailuireApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const deleteLocation = useCallback(
    async (cId: number, pId: number, onSuccess?: () => void, onFailure?: () => void) => {
      try {
        dispatch(deleteLocationInitApiAction());
        await locationApiService.deleteLocation(cId, pId);
        dispatch(deleteLocationSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(deleteLocationFailuireApiAction());
      }
    },
    [dispatch]
  );

  const setLocation = useCallback(
    (business: Location | null) => {
      dispatch(setLocationAction(business));
    },
    [dispatch]
  );

  const increasePage = useCallback(() => {
    dispatch(increasePageAction());
  }, [dispatch]);

  const clearLocation = useCallback(() => {
    dispatch(businessClearAction());
  }, [dispatch]);

  return {
    locations,
    location,
    total,
    page,
    noMore,
    errorCode,
    getLocations,
    postLocation,
    putLocation,
    deleteLocation,
    setLocation,
    increasePage,
    clearLocation,
  };
}
